import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { TbArrowLeft } from "react-icons/tb";
import RegistrationForm from "../../../components/RegistrationForm/RegistrationForm";
import LoginForm from "../../../components/LoginForm/LoginForm";
import SocialLoginButtons from "../../../components/LoginStrategies/SocialLoginButtons";
import * as translator from "../../../utils/translator";
import CarePlusStepper from "../../CarePlusShared/CarePlusStepper";
import CarePlusShippingFAQ from "../../CarePlusShared/CarePlusShippingFAQ";
import { showsCarePlusShippingFAQ } from "../../CarePlusShared/showsCarePlusShippingFAQ";
import "./RegistrationV3.css";

interface RegistrationV3Props {
  country: string;
  language: string;
  redirectPath: string;
  cameFromPath: string;
  customerVerificationType?: string;
  cartSummary: ReactNode;
  setStickyNotificationDetails: (value: any) => void;
  onAuthenticated: () => void;
}

const RegistrationV3: React.FC<RegistrationV3Props> = ({
  country,
  language,
  redirectPath,
  cameFromPath,
  customerVerificationType,
  cartSummary,
  setStickyNotificationDetails,
  onAuthenticated,
}) => {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [existingEmail, setExistingEmail] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  const handleAuthenticated = () => {
    setAuthenticated(true);
    onAuthenticated();
  };

  const switchToLogin = () => {
    setExistingEmail(false);
    setShowLogin(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (authenticated) {
    return null;
  }

  const socialLogin = (
    <>
      <SocialLoginButtons
        redirectPath={redirectPath}
        cameFromPath={cameFromPath}
        customerVerificationType={customerVerificationType}
        language={language}
        country={country}
        onCompleteSignup={handleAuthenticated}
        onFailureSignup={() => setStickyNotificationDetails({
          show: true,
          title: translator.translate(language, "default", "StickyNotification/danger_title"),
          content: translator.translate(language, "default", "StickyNotification/oauth_failed"),
          level: "error",
          showInfoIcon: true,
          refresh: false,
        })}
      />
      <div className="registration-v3__divider" aria-hidden="true">
        <span />
        {translator.translate(language, "authentication", "RegistrationPage/or")}
        <span />
      </div>
    </>
  );

  return (
    <section className="registration-v3" aria-label={translator.translate(language, "default", "RegistrationForm/title")}>
      <div className="registration-v3__progress" aria-label="Checkout progress">
        <button
          type="button"
          className="registration-v3__back"
          aria-label={translator.translate(language, "choose_plan_care_plus", "back") || "Back"}
          onClick={() => navigate(-1)}
        >
          <TbArrowLeft size={22} aria-hidden="true" />
        </button>
        <div className="registration-v3__stepper">
          <CarePlusStepper
            current="checkout"
            t={(key, fallback) => translator.translate(language, "choose_plan_care_plus", key) || fallback}
            compact
            variant="white"
          />
        </div>
        <div className="registration-v3__header-spacer" aria-hidden="true" />
      </div>
      <h1 className="registration-v3__shipping-title">
        {translator.translate(language, "shipping", "ShippingPage/main_title")}
      </h1>
      <div className="registration-v3__card">
        {showLogin ? (
          <>
            <h1>{translator.translate(language, "default", "LoginQuestion/login_title")}</h1>
            {socialLogin}
            <LoginForm
              language={language}
              country={country}
              setStickyNotificationDetails={setStickyNotificationDetails}
              submitButtonPlaceholder={translator.translate(language, "default", "LoginQuestion/login_and_continue")}
              onLoginSuccess={handleAuthenticated}
            />
            <p className="registration-v3__login-prompt">
              <button type="button" className="registration-v3__switch" onClick={() => setShowLogin(false)}>
                {translator.translate(language, "default", "RegistrationForm/title")}
              </button>
            </p>
          </>
        ) : (
          <>
            <header className="registration-v3__intro">
              <h1>{translator.translate(language, "authentication", "RegistrationPage/registration_form_title")}</h1>
              <p>{translator.translate(language, "authentication", "RegistrationPage/registration_form_subtitle")}</p>
            </header>
            {existingEmail && (
              <p className="registration-v3__error" role="alert" aria-live="polite">
                {translator.translate(language, "default", "StickyNotification/email_already_taken")}
              </p>
            )}
            {socialLogin}
            <RegistrationForm
              navigationCallback={handleAuthenticated}
              setStickyNotificationDetails={setStickyNotificationDetails}
              languageProp={language}
              submitButtonPlaceholder={translator.translate(language, "default", "RegistrationForm/submit_button_text")}
              redirectPath={redirectPath}
              customerVerificationType={customerVerificationType}
              cameFromPath={cameFromPath}
              isCannabisCatalog={false}
              initialGender="1"
              showSocialLogin={false}
              birthdayDatePicker
              onExistingEmail={() => setExistingEmail(true)}
            />
            <p className="registration-v3__login-prompt">
              {translator.translate(language, "authentication", "RegistrationPage/already_a_customer")} {" "}
              <button type="button" className="registration-v3__switch" onClick={switchToLogin}>
                {translator.translate(language, "authentication", "RegistrationPage/login_here")}
              </button>
            </p>
          </>
        )}
      </div>
      {cartSummary && <div className="registration-v3__summary">{cartSummary}</div>}
      {showsCarePlusShippingFAQ(country) && (
        <div className="registration-v3__faq">
          <CarePlusShippingFAQ
            t={(key, fallback) => translator.translate(language, "choose_plan_care_plus", key) || fallback}
          />
        </div>
      )}
    </section>
  );
};

export default RegistrationV3;
